'use client';

import { useState } from 'react';
import Breadcrumb from '@/components/ui/Breadcrumb';
import Link from "next/link";
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { driverApplicationService, Reference } from '@/lib/services/driverApplicationService';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

export default function BeADriverPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [dobError, setDobError] = useState('');

  // Structured Data Schemas
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Be a Driver - Join GoGedit Driver Team',
    description: 'Apply to become a GoGedit driver. Flexible schedule, competitive earnings, and be part of Ontario\'s leading on-demand moving and delivery platform.',
    url: `${SITE_URL}/be-a-driver`,
    breadcrumb: generateBreadcrumbSchema([
    { name: 'Home', url: SITE_URL },
    { name: 'Be a Driver', url: `${SITE_URL}/be-a-driver` }]
    )
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GoGedit',
    url: SITE_URL,
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17fa56639-1772554554367.png",
    description: 'On-demand moving and delivery services for urban logistics',
    sameAs: [
    'https://www.facebook.com/gogedit',
    'https://twitter.com/gogedit',
    'https://www.linkedin.com/company/gogedit',
    'https://www.instagram.com/gogedit'],

    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Recruitment',
      availableLanguage: ['English', 'French']
    }
  };

  const jobPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: 'Delivery and Moving Driver',
    description: 'Join GoGedit as a delivery and moving driver. Enjoy flexible schedules, competitive earnings, and be part of Ontario\'s leading on-demand logistics platform. We\'re looking for reliable drivers with valid licenses and their own vehicles.',
    datePosted: '2026-02-22',
    validThrough: '2026-12-31',
    employmentType: ['CONTRACTOR', 'PART_TIME', 'FULL_TIME'],
    hiringOrganization: {
      '@type': 'Organization',
      name: 'GoGedit',
      sameAs: SITE_URL,
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1334f7942-1767814517715.png"
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'ON',
        addressCountry: 'CA'
      }
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'CAD',
      value: {
        '@type': 'QuantitativeValue',
        minValue: 20,
        maxValue: 35,
        unitText: 'HOUR'
      }
    },
    qualifications: 'Valid driver\'s license (Class G or higher), clean driving record, own vehicle with insurance, ability to lift and move heavy items, excellent customer service skills',
    responsibilities: 'Pick up and deliver items for customers, provide excellent customer service, maintain vehicle cleanliness and safety, follow delivery schedules, communicate effectively with dispatch',
    skills: 'Driving, Customer Service, Time Management, Physical Fitness, Navigation',
    workHours: 'Flexible scheduling available - choose your own hours',
    benefits: 'Flexible schedule, competitive pay, be your own boss, weekly payouts, referral bonuses',
    applicantLocationRequirements: {
      '@type': 'State',
      name: 'Ontario'
    },
    jobLocationType: 'TELECOMMUTE'
  };

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: 'Ontario',
    postalCode: '',
    dateOfBirth: '',
    // Driver's License Information
    licenseNumber: '', licenseClass: 'G', licenseExpiryDate: '', licenseProvince: 'Ontario',
    yearsLicensed: 0,
    // Vehicle Information
    vehicleType: '', vehicleMake: '', vehicleModel: '', vehicleYear: new Date().getFullYear(), licensePlate: '', insuranceProvider: '', insurancePolicyNumber: '', insuranceExpiryDate: '',
    // Background Check Consent
    backgroundCheckConsent: false,
    criminalRecordCheckConsent: false,
    drivingRecordCheckConsent: false,
    // Availability Schedule
    availabilityMonday: [] as string[],
    availabilityTuesday: [] as string[],
    availabilityWednesday: [] as string[],
    availabilityThursday: [] as string[],
    availabilityFriday: [] as string[],
    availabilitySaturday: [] as string[],
    availabilitySunday: [] as string[],
    preferredHoursPerWeek: 0,
    // Driving Experience
    yearsDrivingExperience: 0,
    commercialDrivingExperience: false,
    movingDeliveryExperience: false,
    experienceDescription: '',
    // Screening Questions
    whyJoin: '', customerServiceExperience: '',
    physicalCapabilityConfirmation: false,
    smartphoneAccess: false,
    // References
    reference1Name: '', reference1Phone: '', reference1Relationship: '', reference2Name: '', reference2Phone: '', reference2Relationship: '',
    // Emergency Contact
    emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelationship: '',
    // Terms
    termsAccepted: false,
    privacyPolicyAccepted: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleAvailabilityChange = (day: string, timeSlot: string) => {
    const key = `availability${day}` as keyof typeof formData;
    const currentSlots = formData[key] as string[];

    if (currentSlots.includes(timeSlot)) {
      setFormData({ ...formData, [key]: currentSlots.filter((slot) => slot !== timeSlot) });
    } else {
      setFormData({ ...formData, [key]: [...currentSlots, timeSlot] });
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1: {
        const basicFields = !!(formData.firstName && formData.lastName && formData.email && formData.phone &&
        formData.address && formData.city && formData.province && formData.postalCode && formData.dateOfBirth);
        if (!basicFields) return false;
        if (formData.dateOfBirth) {
          const dob = new Date(formData.dateOfBirth);
          const today = new Date();
          const minAge = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
          if (dob > minAge) return false;
        }
        return true;
      }
      case 2:
        return !!(formData.licenseNumber && formData.licenseClass && formData.licenseExpiryDate &&
        formData.licenseProvince && formData.yearsLicensed > 0);
      case 3:
        return !!(formData.vehicleType && formData.vehicleMake && formData.vehicleModel &&
        formData.vehicleYear && formData.licensePlate && formData.insuranceProvider &&
        formData.insurancePolicyNumber && formData.insuranceExpiryDate);
      case 4:
        return formData.backgroundCheckConsent && formData.criminalRecordCheckConsent && formData.drivingRecordCheckConsent;
      case 5:
        return true; // Availability is optional
      case 6:
        return !!(formData.yearsDrivingExperience > 0 && formData.whyJoin);
      case 7:
        return !!(formData.emergencyContactName && formData.emergencyContactPhone && formData.emergencyContactRelationship);
      case 8:
        return formData.termsAccepted && formData.privacyPolicyAccepted && formData.physicalCapabilityConfirmation && formData.smartphoneAccess;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && formData.dateOfBirth) {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      const minAge = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
      if (dob > minAge) {
        setDobError('You must be at least 18 years old to apply.');
        return;
      } else {
        setDobError('');
      }
    }
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert('Please fill in all required fields before proceeding.');
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(8)) {
      alert('Please complete all required fields and accept the terms.');
      return;
    }

    setLoading(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const references: Reference[] = [];
      if (formData.reference1Name && formData.reference1Phone) {
        references.push({
          name: formData.reference1Name,
          phone: formData.reference1Phone,
          relationship: formData.reference1Relationship
        });
      }
      if (formData.reference2Name && formData.reference2Phone) {
        references.push({
          name: formData.reference2Name,
          phone: formData.reference2Phone,
          relationship: formData.reference2Relationship
        });
      }

      const availabilitySchedule: Record<string, string[]> = {
        monday: formData.availabilityMonday,
        tuesday: formData.availabilityTuesday,
        wednesday: formData.availabilityWednesday,
        thursday: formData.availabilityThursday,
        friday: formData.availabilityFriday,
        saturday: formData.availabilitySaturday,
        sunday: formData.availabilitySunday
      };

      const result = await driverApplicationService.submitApplication({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode,
        dateOfBirth: formData.dateOfBirth,
        licenseNumber: formData.licenseNumber,
        licenseClass: formData.licenseClass,
        licenseExpiryDate: formData.licenseExpiryDate,
        licenseProvince: formData.licenseProvince,
        yearsLicensed: Number(formData.yearsLicensed),
        vehicleType: formData.vehicleType,
        vehicleMake: formData.vehicleMake,
        vehicleModel: formData.vehicleModel,
        vehicleYear: Number(formData.vehicleYear),
        licensePlate: formData.licensePlate,
        insuranceProvider: formData.insuranceProvider,
        insurancePolicyNumber: formData.insurancePolicyNumber,
        insuranceExpiryDate: formData.insuranceExpiryDate,
        backgroundCheckConsent: formData.backgroundCheckConsent,
        criminalRecordCheckConsent: formData.criminalRecordCheckConsent,
        drivingRecordCheckConsent: formData.drivingRecordCheckConsent,
        availabilitySchedule,
        preferredHoursPerWeek: formData.preferredHoursPerWeek ? Number(formData.preferredHoursPerWeek) : undefined,
        yearsDrivingExperience: Number(formData.yearsDrivingExperience),
        commercialDrivingExperience: formData.commercialDrivingExperience,
        movingDeliveryExperience: formData.movingDeliveryExperience,
        experienceDescription: formData.experienceDescription || undefined,
        whyJoin: formData.whyJoin,
        customerServiceExperience: formData.customerServiceExperience || undefined,
        physicalCapabilityConfirmation: formData.physicalCapabilityConfirmation,
        smartphoneAccess: formData.smartphoneAccess,
        references,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        emergencyContactRelationship: formData.emergencyContactRelationship,
        termsAccepted: formData.termsAccepted,
        privacyPolicyAccepted: formData.privacyPolicyAccepted
      });

      if (result) {
        setSubmitStatus('success');
        setCurrentStep(9); // Success step

        // Send confirmation email — fire-and-forget, does not block success flow
        fetch('/api/email/application-confirmation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            applicationType: 'driver',
            applicantDetails: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              city: formData.city,
              province: formData.province,
              vehicleType: formData.vehicleType,
              vehicleMake: formData.vehicleMake,
              vehicleModel: formData.vehicleModel,
              vehicleYear: Number(formData.vehicleYear),
              licenseClass: formData.licenseClass,
              yearsLicensed: Number(formData.yearsLicensed),
            },
          }),
        }).catch((emailErr) => {
          console.warn('[Email] Failed to send driver confirmation email:', emailErr);
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage('Failed to submit your application. Please try again.');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setErrorMessage(error?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: `${SITE_URL}/home-page` },
  { name: 'Be a Driver', url: `${SITE_URL}/be-a-driver` }]
  );

  const timeSlots = ['morning', 'afternoon', 'evening'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingSchema) }} />
      
      <div className="min-h-screen bg-white">
        <NavBar />
        
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[{ label: 'Be a Driver' }]} />
            </div>

            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-navy-dark mb-4">Be a Driver with GoGedit</h1>
              <p className="text-xl text-body-text">
                Join Ontario's leading on-demand moving and delivery platform
              </p>
            </div>

            {/* Progress Bar */}
            {currentStep < 9 &&
            <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-navy-dark">Step {currentStep} of 8</span>
                  <span className="text-sm text-body-text">{Math.round(currentStep / 8 * 100)}% Complete</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                  className="bg-brand-green h-2 rounded-full transition-all duration-300"
                  style={{ width: `${currentStep / 8 * 100}%` }}>
                </div>
                </div>
              </div>
            }

            {submitStatus === 'error' &&
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
                {errorMessage}
              </div>
            }

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Personal Information */}
                {currentStep === 1 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Personal Information</h2>
                      <p className="text-body-text mb-6">Let's start with your basic information</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-navy-dark mb-2">
                          First Name *
                        </label>
                        <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-navy-dark mb-2">
                          Last Name *
                        </label>
                        <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-navy-dark mb-2">
                          Email Address *
                        </label>
                        <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-navy-dark mb-2">
                          Phone Number *
                        </label>
                        <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="416-555-0123"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-navy-dark mb-2">
                        Street Address *
                      </label>
                      <input
                      type="text"
                      id="address"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-navy-dark mb-2">
                          City *
                        </label>
                        <input
                        type="text"
                        id="city"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="province" className="block text-sm font-medium text-navy-dark mb-2">
                          Province *
                        </label>
                        <select
                        id="province"
                        name="province"
                        required
                        value={formData.province}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent">
                        
                          <option value="Ontario">Ontario</option>
                          <option value="Quebec">Quebec</option>
                          <option value="British Columbia">British Columbia</option>
                          <option value="Alberta">Alberta</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="postalCode" className="block text-sm font-medium text-navy-dark mb-2">
                          Postal Code *
                        </label>
                        <input
                        type="text"
                        id="postalCode"
                        name="postalCode"
                        required
                        value={formData.postalCode}
                        onChange={handleChange}
                        placeholder="M5V 2T6"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                    </div>

                    <div>
                      <label htmlFor="dateOfBirth" className="block text-sm font-medium text-navy-dark mb-2">
                        Date of Birth *
                      </label>
                      <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      required
                      min="1920-01-01"
                      max={new Date().toISOString().split('T')[0]}
                      value={formData.dateOfBirth}
                      onChange={(e) => { handleChange(e); setDobError(''); }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent ${dobError ? 'border-red-500' : 'border-gray-300'}`} />
                    
                      {dobError && <p className="text-sm text-red-600 mt-1">{dobError}</p>}
                      {!dobError && <p className="text-sm text-body-text mt-1">You must be at least 18 years old</p>}
                    </div>
                  </div>
                }

                {/* Step 2: Driver's License Information */}
                {currentStep === 2 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Driver's License Information</h2>
                      <p className="text-body-text mb-6">Provide your valid driver's license details</p>
                    </div>

                    <div>
                      <label htmlFor="licenseNumber" className="block text-sm font-medium text-navy-dark mb-2">
                        Driver's License Number *
                      </label>
                      <input
                      type="text"
                      id="licenseNumber"
                      name="licenseNumber"
                      required
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="licenseClass" className="block text-sm font-medium text-navy-dark mb-2">
                          License Class *
                        </label>
                        <select
                        id="licenseClass"
                        name="licenseClass"
                        required
                        value={formData.licenseClass}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent">
                        
                          <option value="G">G - Full License</option>
                          <option value="G2">G2 - Intermediate License</option>
                          <option value="D">D - Commercial License</option>
                          <option value="C">C - Truck License</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="licenseProvince" className="block text-sm font-medium text-navy-dark mb-2">
                          License Province *
                        </label>
                        <select
                        id="licenseProvince"
                        name="licenseProvince"
                        required
                        value={formData.licenseProvince}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent">
                        
                          <option value="Ontario">Ontario</option>
                          <option value="Quebec">Quebec</option>
                          <option value="British Columbia">British Columbia</option>
                          <option value="Alberta">Alberta</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="licenseExpiryDate" className="block text-sm font-medium text-navy-dark mb-2">
                          License Expiry Date *
                        </label>
                        <input
                        type="date"
                        id="licenseExpiryDate"
                        name="licenseExpiryDate"
                        required
                        value={formData.licenseExpiryDate}
                        onChange={handleChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="yearsLicensed" className="block text-sm font-medium text-navy-dark mb-2">
                          Years Licensed *
                        </label>
                        <input
                        type="number"
                        id="yearsLicensed"
                        name="yearsLicensed"
                        required
                        min="1"
                        value={formData.yearsLicensed}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                    </div>
                  </div>
                }

                {/* Step 3: Vehicle Information */}
                {currentStep === 3 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Vehicle Information</h2>
                      <p className="text-body-text mb-6">Tell us about your vehicle</p>
                    </div>

                    <div>
                      <label htmlFor="vehicleType" className="block text-sm font-medium text-navy-dark mb-2">
                        Vehicle Type *
                      </label>
                      <select
                      id="vehicleType"
                      name="vehicleType"
                      required
                      value={formData.vehicleType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent">
                      
                        <option value="">Select vehicle type</option>
                        <option value="pickup_truck">Pickup Truck</option>
                        <option value="van">Van</option>
                        <option value="cargo_van">Cargo Van</option>
                        <option value="box_truck">Box Truck</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label htmlFor="vehicleMake" className="block text-sm font-medium text-navy-dark mb-2">
                          Make *
                        </label>
                        <input
                        type="text"
                        id="vehicleMake"
                        name="vehicleMake"
                        required
                        value={formData.vehicleMake}
                        onChange={handleChange}
                        placeholder="Ford"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="vehicleModel" className="block text-sm font-medium text-navy-dark mb-2">
                          Model *
                        </label>
                        <input
                        type="text"
                        id="vehicleModel"
                        name="vehicleModel"
                        required
                        value={formData.vehicleModel}
                        onChange={handleChange}
                        placeholder="F-150"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="vehicleYear" className="block text-sm font-medium text-navy-dark mb-2">
                          Year *
                        </label>
                        <input
                        type="number"
                        id="vehicleYear"
                        name="vehicleYear"
                        required
                        min="2000"
                        max={new Date().getFullYear() + 1}
                        value={formData.vehicleYear}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                    </div>

                    <div>
                      <label htmlFor="licensePlate" className="block text-sm font-medium text-navy-dark mb-2">
                        License Plate *
                      </label>
                      <input
                      type="text"
                      id="licensePlate"
                      name="licensePlate"
                      required
                      value={formData.licensePlate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>

                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="text-lg font-semibold text-navy-dark mb-4">Insurance Information</h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="insuranceProvider" className="block text-sm font-medium text-navy-dark mb-2">
                          Insurance Provider *
                        </label>
                        <input
                        type="text"
                        id="insuranceProvider"
                        name="insuranceProvider"
                        required
                        value={formData.insuranceProvider}
                        onChange={handleChange}
                        placeholder="State Farm"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                      <div>
                        <label htmlFor="insurancePolicyNumber" className="block text-sm font-medium text-navy-dark mb-2">
                          Policy Number *
                        </label>
                        <input
                        type="text"
                        id="insurancePolicyNumber"
                        name="insurancePolicyNumber"
                        required
                        value={formData.insurancePolicyNumber}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                      
                      </div>
                    </div>

                    <div>
                      <label htmlFor="insuranceExpiryDate" className="block text-sm font-medium text-navy-dark mb-2">
                        Insurance Expiry Date *
                      </label>
                      <input
                      type="date"
                      id="insuranceExpiryDate"
                      name="insuranceExpiryDate"
                      required
                      value={formData.insuranceExpiryDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>
                  </div>
                }

                {/* Step 4: Background Check Consent */}
                {currentStep === 4 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Background Check Consent</h2>
                      <p className="text-body-text mb-6">We require your consent to conduct necessary background checks</p>
                    </div>

                    <div className="bg-blue-light rounded-lg p-6 space-y-4">
                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="backgroundCheckConsent"
                        name="backgroundCheckConsent"
                        required
                        checked={formData.backgroundCheckConsent}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="backgroundCheckConsent" className="text-navy-dark">
                          <span className="font-semibold">General Background Check *</span>
                          <p className="text-sm text-body-text mt-1">
                            I consent to GoGedit conducting a comprehensive background check including employment history and references.
                          </p>
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="criminalRecordCheckConsent"
                        name="criminalRecordCheckConsent"
                        required
                        checked={formData.criminalRecordCheckConsent}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="criminalRecordCheckConsent" className="text-navy-dark">
                          <span className="font-semibold">Criminal Record Check *</span>
                          <p className="text-sm text-body-text mt-1">
                            I consent to a criminal record check as required by law for commercial driving positions.
                          </p>
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="drivingRecordCheckConsent"
                        name="drivingRecordCheckConsent"
                        required
                        checked={formData.drivingRecordCheckConsent}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="drivingRecordCheckConsent" className="text-navy-dark">
                          <span className="font-semibold">Driving Record Check *</span>
                          <p className="text-sm text-body-text mt-1">
                            I consent to a review of my driving record including any violations, suspensions, or accidents.
                          </p>
                        </label>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-navy-dark">
                        <strong>Note:</strong> All background checks are conducted in compliance with applicable privacy laws. 
                        Your information will be kept confidential and used solely for employment screening purposes.
                      </p>
                    </div>
                  </div>
                }

                {/* Step 5: Availability Schedule */}
                {currentStep === 5 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Availability Schedule</h2>
                      <p className="text-body-text mb-6">Select your preferred working hours</p>
                    </div>

                    <div className="space-y-4">
                      {days.map((day) =>
                    <div key={day} className="border border-gray-200 rounded-lg p-4">
                          <h3 className="font-semibold text-navy-dark mb-3">{day}</h3>
                          <div className="flex flex-wrap gap-3">
                            {timeSlots.map((slot) => {
                          const key = `availability${day}` as keyof typeof formData;
                          const isSelected = (formData[key] as string[]).includes(slot);
                          return (
                            <button
                              key={slot}
                              type="button"
                              onClick={() => handleAvailabilityChange(day, slot)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              isSelected ?
                              'bg-brand-green text-white' : 'bg-gray-100 text-navy-dark hover:bg-gray-200'}`
                              }>
                              
                                  {slot.charAt(0).toUpperCase() + slot.slice(1)}
                                </button>);

                        })}
                          </div>
                        </div>
                    )}
                    </div>

                    <div>
                      <label htmlFor="preferredHoursPerWeek" className="block text-sm font-medium text-navy-dark mb-2">
                        Preferred Hours Per Week (Optional)
                      </label>
                      <input
                      type="number"
                      id="preferredHoursPerWeek"
                      name="preferredHoursPerWeek"
                      min="0"
                      max="60"
                      value={formData.preferredHoursPerWeek}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>
                  </div>
                }

                {/* Step 6: Driving Experience & Screening Questions */}
                {currentStep === 6 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Experience & Qualifications</h2>
                      <p className="text-body-text mb-6">Tell us about your driving and work experience</p>
                    </div>

                    <div>
                      <label htmlFor="yearsDrivingExperience" className="block text-sm font-medium text-navy-dark mb-2">
                        Years of Driving Experience *
                      </label>
                      <input
                      type="number"
                      id="yearsDrivingExperience"
                      name="yearsDrivingExperience"
                      required
                      min="1"
                      value={formData.yearsDrivingExperience}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                        type="checkbox"
                        id="commercialDrivingExperience"
                        name="commercialDrivingExperience"
                        checked={formData.commercialDrivingExperience}
                        onChange={handleChange}
                        className="mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="commercialDrivingExperience" className="text-navy-dark">
                          I have commercial driving experience
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                        type="checkbox"
                        id="movingDeliveryExperience"
                        name="movingDeliveryExperience"
                        checked={formData.movingDeliveryExperience}
                        onChange={handleChange}
                        className="mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="movingDeliveryExperience" className="text-navy-dark">
                          I have moving or delivery experience
                        </label>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="experienceDescription" className="block text-sm font-medium text-navy-dark mb-2">
                        Describe Your Relevant Experience (Optional)
                      </label>
                      <textarea
                      id="experienceDescription"
                      name="experienceDescription"
                      rows={4}
                      value={formData.experienceDescription}
                      onChange={handleChange}
                      placeholder="Tell us about your previous driving, moving, or delivery experience..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>

                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="text-lg font-semibold text-navy-dark mb-4">Screening Questions</h3>
                    </div>

                    <div>
                      <label htmlFor="whyJoin" className="block text-sm font-medium text-navy-dark mb-2">
                        Why do you want to join GoGedit as a driver? *
                      </label>
                      <textarea
                      id="whyJoin"
                      name="whyJoin"
                      required
                      rows={4}
                      value={formData.whyJoin}
                      onChange={handleChange}
                      placeholder="Share your motivation for joining our team..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>

                    <div>
                      <label htmlFor="customerServiceExperience" className="block text-sm font-medium text-navy-dark mb-2">
                        Describe Your Customer Service Experience (Optional)
                      </label>
                      <textarea
                      id="customerServiceExperience"
                      name="customerServiceExperience"
                      rows={4}
                      value={formData.customerServiceExperience}
                      onChange={handleChange}
                      placeholder="Tell us about your customer service skills and experience..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                    
                    </div>
                  </div>
                }

                {/* Step 7: References & Emergency Contact */}
                {currentStep === 7 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">References & Emergency Contact</h2>
                      <p className="text-body-text mb-6">Provide professional references and emergency contact information</p>
                    </div>

                    <div className="border-b border-gray-200 pb-6">
                      <h3 className="text-lg font-semibold text-navy-dark mb-4">Professional References (Optional)</h3>
                      <p className="text-sm text-body-text mb-4">Provide at least one professional reference</p>

                      <div className="space-y-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-navy-dark mb-3">Reference 1</h4>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <label htmlFor="reference1Name" className="block text-sm font-medium text-navy-dark mb-2">
                                Name
                              </label>
                              <input
                              type="text"
                              id="reference1Name"
                              name="reference1Name"
                              value={formData.reference1Name}
                              onChange={handleChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                            
                            </div>
                            <div>
                              <label htmlFor="reference1Phone" className="block text-sm font-medium text-navy-dark mb-2">
                                Phone
                              </label>
                              <input
                              type="tel"
                              id="reference1Phone"
                              name="reference1Phone"
                              value={formData.reference1Phone}
                              onChange={handleChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                            
                            </div>
                            <div>
                              <label htmlFor="reference1Relationship" className="block text-sm font-medium text-navy-dark mb-2">
                                Relationship
                              </label>
                              <input
                              type="text"
                              id="reference1Relationship"
                              name="reference1Relationship"
                              value={formData.reference1Relationship}
                              onChange={handleChange}
                              placeholder="Former Manager"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                            
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-navy-dark mb-3">Reference 2</h4>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <label htmlFor="reference2Name" className="block text-sm font-medium text-navy-dark mb-2">
                                Name
                              </label>
                              <input
                              type="text"
                              id="reference2Name"
                              name="reference2Name"
                              value={formData.reference2Name}
                              onChange={handleChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                            
                            </div>
                            <div>
                              <label htmlFor="reference2Phone" className="block text-sm font-medium text-navy-dark mb-2">
                                Phone
                              </label>
                              <input
                              type="tel"
                              id="reference2Phone"
                              name="reference2Phone"
                              value={formData.reference2Phone}
                              onChange={handleChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                            
                            </div>
                            <div>
                              <label htmlFor="reference2Relationship" className="block text-sm font-medium text-navy-dark mb-2">
                                Relationship
                              </label>
                              <input
                              type="text"
                              id="reference2Relationship"
                              name="reference2Relationship"
                              value={formData.reference2Relationship}
                              onChange={handleChange}
                              placeholder="Colleague"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                            
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-navy-dark mb-4">Emergency Contact *</h3>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <label htmlFor="emergencyContactName" className="block text-sm font-medium text-navy-dark mb-2">
                            Name *
                          </label>
                          <input
                          type="text"
                          id="emergencyContactName"
                          name="emergencyContactName"
                          required
                          value={formData.emergencyContactName}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                        
                        </div>
                        <div>
                          <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-navy-dark mb-2">
                            Phone *
                          </label>
                          <input
                          type="tel"
                          id="emergencyContactPhone"
                          name="emergencyContactPhone"
                          required
                          value={formData.emergencyContactPhone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                        
                        </div>
                        <div>
                          <label htmlFor="emergencyContactRelationship" className="block text-sm font-medium text-navy-dark mb-2">
                            Relationship *
                          </label>
                          <input
                          type="text"
                          id="emergencyContactRelationship"
                          name="emergencyContactRelationship"
                          required
                          value={formData.emergencyContactRelationship}
                          onChange={handleChange}
                          placeholder="Spouse, Parent, Sibling"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent" />
                        
                        </div>
                      </div>
                    </div>
                  </div>
                }

                {/* Step 8: Terms & Conditions */}
                {currentStep === 8 &&
                <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Terms & Conditions</h2>
                      <p className="text-body-text mb-6">Please review and accept our terms to complete your application</p>
                    </div>

                    <div className="bg-blue-light rounded-lg p-6 space-y-4">
                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="physicalCapabilityConfirmation"
                        name="physicalCapabilityConfirmation"
                        required
                        checked={formData.physicalCapabilityConfirmation}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="physicalCapabilityConfirmation" className="text-navy-dark">
                          <span className="font-semibold">Physical Capability *</span>
                          <p className="text-sm text-body-text mt-1">
                            I confirm that I am physically capable of lifting and moving items at least 100 lbs and performing the duties required of a cargo driver.
                          </p>
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="smartphoneAccess"
                        name="smartphoneAccess"
                        required
                        checked={formData.smartphoneAccess}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="smartphoneAccess" className="text-navy-dark">
                          <span className="font-semibold">Smartphone Access *</span>
                          <p className="text-sm text-body-text mt-1">
                            I have access to a smartphone with data plan for using the GoGedit driver app.
                          </p>
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="termsAccepted"
                        name="termsAccepted"
                        required
                        checked={formData.termsAccepted}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="termsAccepted" className="text-navy-dark">
                          <span className="font-semibold">Terms and Conditions *</span>
                          <p className="text-sm text-body-text mt-1">
                            I have read and agree to the{' '}
                            <Link href="/terms-and-conditions" target="_blank" className="text-brand-green hover:underline">
                              Terms and Conditions
                            </Link>
                            {' '}for GoGedit drivers.
                          </p>
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                        type="checkbox"
                        id="privacyPolicyAccepted"
                        name="privacyPolicyAccepted"
                        required
                        checked={formData.privacyPolicyAccepted}
                        onChange={handleChange}
                        className="mt-1 mr-3 h-5 w-5 text-brand-green focus:ring-brand-green border-gray-300 rounded" />
                      
                        <label htmlFor="privacyPolicyAccepted" className="text-navy-dark">
                          <span className="font-semibold">Privacy Policy *</span>
                          <p className="text-sm text-body-text mt-1">
                            I have read and agree to the{' '}
                            <Link href="/privacy-policy" target="_blank" className="text-brand-green hover:underline">
                              Privacy Policy
                            </Link>
                            {' '}and understand how my personal information will be used.
                          </p>
                        </label>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-navy-dark">
                        <strong>Ready to submit?</strong> Once you submit your application, our team will review it within 3-5 business days. 
                        You'll receive an email notification about the status of your application.
                      </p>
                    </div>
                  </div>
                }

                {/* Step 9: Success Message */}
                {currentStep === 9 &&
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h2 className="text-3xl font-bold text-navy-dark mb-4">Application Submitted Successfully!</h2>
                    <p className="text-lg text-body-text mb-8">
                      Thank you for applying to be a GoGedit driver. We've received your application and will review it carefully.
                    </p>
                    <div className="bg-blue-light rounded-lg p-6 mb-8">
                      <h3 className="font-semibold text-navy-dark mb-2">What's Next?</h3>
                      <ul className="text-left text-body-text space-y-2">
                        <li className="flex items-start">
                          <span className="text-brand-green mr-2">✓</span>
                          Our team will review your application within 3-5 business days
                        </li>
                        <li className="flex items-start">
                          <span className="text-brand-green mr-2">✓</span>
                          You'll receive an email notification about your application status
                        </li>
                        <li className="flex items-start">
                          <span className="text-brand-green mr-2">✓</span>
                          If approved, we'll contact you to schedule an orientation session
                        </li>
                        <li className="flex items-start">
                          <span className="text-brand-green mr-2">✓</span>
                          Check your email regularly for updates from our team
                        </li>
                      </ul>
                    </div>
                    <Link
                    href="/home-page"
                    className="inline-block bg-brand-green text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all">
                    
                      Return to Home
                    </Link>
                  </div>
                }

                {/* Navigation Buttons */}
                {currentStep < 9 &&
                <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                    {currentStep > 1 ?
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-6 py-3 border-2 border-gray-300 text-navy-dark rounded-full font-medium hover:bg-gray-50 transition-all">
                    
                        Previous
                      </button> :

                  <div></div>
                  }

                    {currentStep < 8 ?
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-8 py-3 bg-brand-green text-white rounded-full font-medium hover:bg-opacity-90 transition-all">
                    
                        Next Step
                      </button> :

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-brand-purple text-white rounded-full font-medium hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    
                        {loading ? 'Submitting...' : 'Submit Application'}
                      </button>
                  }
                  </div>
                }
              </form>
            </div>
          </div>
        </div>

        <FooterSection />
      </div>
    </>);

}